import React, { useState, useEffect } from 'react';
import {
  Users,
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  Ban,
  CheckCircle,
  XCircle,
  Clock,
  Wallet,
  Activity,
  TrendingUp,
  Shield,
  Plus,
  Trash2,
  Save,
  X,
  AlertTriangle,
  UserPlus,
  Settings,
  Lock,
  Unlock,
  Gift
} from 'lucide-react';
import voltronApiService from '../../services/voltronApiService';
import voltronWalletService from '../../services/voltronWalletService';
import type { UserProfile, AssetInfo } from '../../services/voltronApiService';
import UserDetailPage from './UserDetailPage';
import type { User, UserAsset, UserTransaction, UserEvent, ReferralNode, UserStats } from '../../types/user';
import { buildApiUrl } from '../../config';

const UserManagementPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<UserStats>({
    totalUsers: 0,
    activeUsers: 0,
    newUsersToday: 0,
    suspendedUsers: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [showMissingUsersOnly, setShowMissingUsersOnly] = useState(false);
  const [missingUsers, setMissingUsers] = useState<User[]>([]);
  const [loadingMissingUsers, setLoadingMissingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<'balance' | 'referrals' | 'recent' | 'volume'>('recent');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [allUsers, setAllUsers] = useState<User[]>([]); // 전체 유저 데이터 저장
  const [totalUsersCount, setTotalUsersCount] = useState(0);
  // 페이징: 한 페이지에 표시할 유저 수
  const usersPerPage = 100;
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userAssets, setUserAssets] = useState<UserAsset[]>([]);
  const [userTransactions, setUserTransactions] = useState<UserTransaction[]>([]);
  const [userEvents, setUserEvents] = useState<UserEvent[]>([]);
  const [referralTree, setReferralTree] = useState<ReferralNode | null>(null);
  const [newUser, setNewUser] = useState<Partial<User>>({
    userCode: '',
    nickname: '',
    address: '',
    status: 'active',
    tier: 'bronze',
    commissionRate: 0,
    bonusBalance: 0,
    penaltyAmount: 0,
    totalTransactions: 0,
    totalVolume: 0,
    stakingAmount: 0,
    referralCount: 0,
    boomParticipation: 0,
    energyStations: 0,
    rewardsEarned: 0,
    email: '',
    phone: '',
    country: 'KR',
    kycStatus: 'pending',
    referralCode: '',
    referredBy: '',
    vttnBalance: 0,
    vtmBalance: 0,
    usdtBalance: 0,
    lockedBalance: 0,
    welcomeAirdropReceived: false,
    welcomeEventCompleted: false,
    biometricEnabled: false,
    ipAddress: '',
    notes: ''
  });
  const [activeDetailTab, setActiveDetailTab] = useState<'info' | 'assets' | 'transactions' | 'referrals' | 'management'>('info');
  const [showCreateWalletOption, setShowCreateWalletOption] = useState(false);
  const [generatedWallet, setGeneratedWallet] = useState<string | null>(null);
  
  // 에어드롭 관련 상태
  const [showAirdropModal, setShowAirdropModal] = useState(false);
  const [airdropData, setAirdropData] = useState({
    recipients: 'all',
    selectedUsers: [] as string[],
    tokenType: 'VTTN',
    amount: 0,
    description: '',
    executeImmediately: true
  });
  
  // 추천인 보상 분배 관련 상태
  const [showReferralRewardModal, setShowReferralRewardModal] = useState(false);
  const [referralRewardData, setReferralRewardData] = useState({
    fromUser: '',
    toUsers: [] as string[],
    tokenType: 'VTTN',
    amount: 0,
    lockupPeriod: 10,
    withdrawDelay: 72,
    description: ''
  });

  // Debounce 검색어
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // 초기 로드 및 검색어/필터 변경 시 전체 데이터 로드
  useEffect(() => {
    loadAllUsers();
  }, [debouncedSearchTerm, statusFilter, tierFilter]);

  // 미지급 회원만 보기
  useEffect(() => {
    if (showMissingUsersOnly) {
      loadMissingUsers();
    }
  }, [showMissingUsersOnly]);

  // 전체 유저 데이터 로드 (검색/필터 적용)
  const loadAllUsers = async () => {
    try {
      setLoading(true);

      // 전체 데이터를 가져오기 위해 큰 limit 사용 (또는 여러 페이지를 순차적으로 로드)
      // 먼저 총 개수를 확인
      const firstPage: any = await voltronApiService.getUsers({
        page: 1,
        limit: 1,
        search: debouncedSearchTerm || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        tier: tierFilter === 'all' ? undefined : tierFilter
      });

      const totalCount = firstPage?.data?.pagination?.total || firstPage?.data?.total || 0;
      setTotalUsersCount(totalCount);

      if (totalCount === 0) {
        setAllUsers([]);
        setUsers([]);
        setLoading(false);
        return;
      }

      // 전체 데이터를 한 번에 가져오기 (최대 50000개까지)
      const maxLimit = Math.min(totalCount, 50000);
      const result: any = await voltronApiService.getUsers({
        page: 1,
        limit: maxLimit,
        search: debouncedSearchTerm || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
        tier: tierFilter === 'all' ? undefined : tierFilter
      });

      if (result && result.success && result.data) {
        // 백엔드 voltron_users 실제 컬럼을 그대로 맵핑 (추측/랜덤/현재시간 사용 금지)
        const rawUsers = result.data.users || [];
        const transformedUsers: User[] = rawUsers.map((user: any, index: number) => ({
          id: user.id?.toString() || `user-${index + 1}`,
          // userCode 는 현재 DB에 없으므로, 관리용으로만 id 기반 생성
          userCode: `USR${(user.id ?? index + 1).toString().padStart(4, '0')}`,
          nickname: user.nickname || `사용자${index + 1}`,
          address: user.walletAddress || user.wallet_address || user.address || '',
          joinedAt: user.createdAt || user.created_at || '',
          lastActive: user.lastLogin || user.last_login || user.updated_at || '',
          status: user.isActive === false ? 'suspended' : 'active',
          tier: (user.tier || 'bronze').toLowerCase(),
          totalTransactions: user.total_transactions ?? 0,
          totalVolume: parseFloat(user.total_volume ?? 0),
          stakingAmount: parseFloat(user.total_staked ?? user.stakedBalance ?? 0),
          referralCode: user.referral_code || undefined,
          referredBy: user.referred_by || undefined,
          referralCount: user.total_referrals ?? 0,
          boomParticipation: user.boom_participation ?? 0,
          energyStations: user.energy_stations ?? 0,
          email: user.email || undefined,
          phone: user.phone || user.phone_number || undefined,
          kycStatus: user.kyc_status || undefined,
          country: user.country || undefined,
          ipAddress: user.last_login_ip || user.ip_address || '',
          rewardsEarned: parseFloat(user.total_earned ?? 0),
          commissionRate: parseFloat(user.commission_rate ?? 0),
          bonusBalance: parseFloat(user.bonus_balance ?? 0),
          penaltyAmount: parseFloat(user.penalty_amount ?? 0),
          vttnBalance: parseFloat(user.vttnBalance ?? user.vttn_balance ?? 0),
          vtmBalance: parseFloat(user.vtmBalance ?? user.vtm_balance ?? 0),
          usdtBalance: parseFloat(user.usdtBalance ?? user.usdt_balance ?? 0),
          lockedBalance: parseFloat(user.lockedBalance ?? user.locked_balance ?? 0),
          notes: user.admin_notes || user.notes || undefined
        }));

        // 통계 데이터 설정 (백엔드 통계가 있으면 우선 사용, 없으면 프론트에서 계산)
        const statsSource = result.data.stats || {};
        const statsData: UserStats = {
          totalUsers: statsSource.totalUsers ?? result.data.pagination?.total ?? transformedUsers.length,
          activeUsers: statsSource.activeUsers ?? transformedUsers.filter(user => user.status === 'active').length,
          newUsersToday:
            statsSource.newUsersToday ??
            transformedUsers.filter(user => {
              const today = new Date();
              const userDate = new Date(user.joinedAt);
              return userDate.toDateString() === today.toDateString();
            }).length,
          suspendedUsers:
            statsSource.suspendedUsers ?? transformedUsers.filter(user => user.status === 'suspended').length
        };

        setAllUsers(transformedUsers);
        setStats(statsData);
        console.log('✅ Voltron 지갑 사용자 데이터 로드 완료:', transformedUsers.length, '명');
      } else {
        console.warn('⚠️ 사용자 데이터가 없습니다. 빈 목록으로 설정합니다.');
        setUsers([]);
        setStats({
          totalUsers: 0,
          activeUsers: 0,
          newUsersToday: 0,
          suspendedUsers: 0
        });
      }
      setLoading(false);
    } catch (error) {
      console.error('❌ 사용자 데이터 로드 실패:', error);
      // 에러 시 빈 데이터로 설정
      setAllUsers([]);
      setUsers([]);
      setStats({
        totalUsers: 0,
        activeUsers: 0,
        newUsersToday: 0,
        suspendedUsers: 0
      });
      setLoading(false);
    }
  };

  // 누락 회원 목록 조회
  const loadMissingUsers = async () => {
    try {
      setLoadingMissingUsers(true);
      const response = await fetch(buildApiUrl(`/admin/airdrop/missing-users?page=1&limit=1000&search=${encodeURIComponent(searchTerm || '')}`));
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.users) {
          const transformedUsers: User[] = result.users.map((user: any) => ({
            id: user.id?.toString() || `user-${user.walletAddress}`,
            userCode: `USR${(user.id ?? 0).toString().padStart(4, '0')}`,
            nickname: user.nickname || 'N/A',
            address: user.walletAddress || '',
            joinedAt: user.createdAt || '',
            lastActive: '',
            status: user.isActive === false ? 'suspended' : 'active',
            tier: (user.tier || 'bronze').toLowerCase(),
            totalTransactions: 0,
            totalVolume: 0,
            stakingAmount: 0,
            referralCode: user.referralCode || undefined,
            referredBy: undefined,
            referralCount: 0,
            boomParticipation: 0,
            energyStations: 0,
            email: user.email || undefined,
            phone: undefined,
            kycStatus: undefined,
            country: undefined,
            ipAddress: '',
            rewardsEarned: 0,
            commissionRate: 0,
            bonusBalance: 0,
            penaltyAmount: 0,
            vttnBalance: user.vttnBalance || 0,
            vtmBalance: 0,
            usdtBalance: 0,
            lockedBalance: 0,
            notes: `잔액상태: ${user.balanceStatus}`
          }));
          setMissingUsers(transformedUsers);
        }
      }
    } catch (error) {
      console.error('누락 회원 목록 로드 실패:', error);
    } finally {
      setLoadingMissingUsers(false);
    }
  };

  // 에어드롭 실행 함수
  const handleAirdrop = async () => {
    try {
      if (!airdropData.amount || airdropData.amount <= 0) {
        alert('에어드롭 금액을 입력해주세요.');
        return;
      }
      
      let targetUsers: User[] = [];
      
      switch (airdropData.recipients) {
        case 'all':
          targetUsers = users.filter(user => user.status === 'active');
          break;
        case 'selected':
          targetUsers = users.filter(user => airdropData.selectedUsers.includes(user.id));
          break;
        case 'specific':
          targetUsers = users.filter(user => user.status === 'active' && user.kycStatus === 'verified');
          break;
      }
      
      if (targetUsers.length === 0) {
        alert('에어드롭 대상 사용자가 없습니다.');
        return;
      }
      
      const response = await fetch(buildApiUrl('/admin/tokens/airdrop'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipients: targetUsers.map(user => user.address),
          amount: airdropData.amount,
          reason: airdropData.description || 'Admin airdrop'
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          alert(`에어드롭이 성공적으로 실행되었습니다.\n대상 사용자: ${targetUsers.length}명\n총 금액: ${(airdropData.amount * targetUsers.length).toLocaleString()} ${airdropData.tokenType}`);
          setShowAirdropModal(false);
          setAirdropData({
            recipients: 'all',
            selectedUsers: [],
            tokenType: 'VTTN',
            amount: 0,
            description: '',
            executeImmediately: true
          });
        } else {
          alert('에어드롭 실행에 실패했습니다.');
        }
      } else {
        alert('에어드롭 API 호출에 실패했습니다.');
      }
    } catch (error) {
      console.error('에어드롭 실행 실패:', error);
      alert('에어드롭 실행 중 오류가 발생했습니다.');
    }
  };
  
  // 추천인 보상 분배 함수
  const handleReferralRewardDistribution = async () => {
    try {
      if (!referralRewardData.fromUser || !referralRewardData.amount) {
        alert('분배할 사용자와 금액을 입력해주세요.');
        return;
      }
      
      const fromUser = users.find(user => user.id === referralRewardData.fromUser);
      
      if (!fromUser) {
        alert('분배할 사용자를 찾을 수 없습니다.');
        return;
      }
      
      if (fromUser.bonusBalance < referralRewardData.amount) {
        alert('분배할 사용자의 보너스 잔액이 부족합니다.');
        return;
      }
      
      const response = await fetch(buildApiUrl('/referrals/distribute-reward'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fromUserAddress: fromUser.address,
          tokenType: referralRewardData.tokenType,
          totalAmount: referralRewardData.amount,
          lockupPeriod: referralRewardData.lockupPeriod,
          withdrawDelay: referralRewardData.withdrawDelay,
          description: referralRewardData.description
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setUsers(prev => prev.map(user => {
            if (user.id === referralRewardData.fromUser) {
              return { ...user, bonusBalance: user.bonusBalance - referralRewardData.amount };
            }
            return user;
          }));
          
          // 성공 토스트 표시
          const toast = document.createElement('div');
          toast.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2';
          toast.innerHTML = `
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <span>추천인 보상이 성공적으로 분배되었습니다! (${referralRewardData.amount.toLocaleString()} ${referralRewardData.tokenType})</span>
          `;
          document.body.appendChild(toast);
          setTimeout(() => document.body.removeChild(toast), 4000);
          
          setShowReferralRewardModal(false);
          setReferralRewardData({
            fromUser: '',
            toUsers: [],
            tokenType: 'VTTN',
            amount: 0,
            lockupPeriod: 10,
            withdrawDelay: 72,
            description: ''
          });
        } else {
          // 실패 토스트 표시
          const toast = document.createElement('div');
          toast.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2';
          toast.innerHTML = `
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
            <span>추천인 보상 분배에 실패했습니다.</span>
          `;
          document.body.appendChild(toast);
          setTimeout(() => document.body.removeChild(toast), 5000);
        }
      } else {
        // API 호출 실패 토스트 표시
        const toast = document.createElement('div');
        toast.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2';
        toast.innerHTML = `
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
          </svg>
          <span>추천인 보상 분배 API 호출에 실패했습니다.</span>
        `;
        document.body.appendChild(toast);
        setTimeout(() => document.body.removeChild(toast), 5000);
      }
    } catch (error) {
       console.error('추천인 보상 분배 실패:', error);
       
       // 오류 토스트 표시
       const toast = document.createElement('div');
       toast.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2';
       toast.innerHTML = `
         <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
           <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
         </svg>
         <span>추천인 보상 분배 중 오류가 발생했습니다.</span>
       `;
       document.body.appendChild(toast);
       setTimeout(() => document.body.removeChild(toast), 5000);
     }
   };

  const handleEditUser = async () => {
    try {
      if (!editingUser) return;

      setUsers(prev => prev.map(user => 
        user.id === editingUser.id ? editingUser : user
      ));
      setShowEditModal(false);
      setEditingUser(null);
      
      // 성공 토스트 표시
      const toast = document.createElement('div');
      toast.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2';
      toast.innerHTML = `
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        <span>사용자 정보가 성공적으로 수정되었습니다!</span>
      `;
      document.body.appendChild(toast);
      setTimeout(() => document.body.removeChild(toast), 3000);
    } catch (error) {
      console.error('사용자 수정 실패:', error);
      
      // 오류 토스트 표시
      const toast = document.createElement('div');
      toast.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2';
      toast.innerHTML = `
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <span>사용자 수정에 실패했습니다.</span>
      `;
      document.body.appendChild(toast);
      setTimeout(() => document.body.removeChild(toast), 5000);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      setUsers(prev => prev.filter(user => user.id !== userId));
      setStats(prev => ({ ...prev, totalUsers: prev.totalUsers - 1 }));
      setShowDeleteModal(false);
      setSelectedUser(null);
      
      // 성공 토스트 표시
      const toast = document.createElement('div');
      toast.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2';
      toast.innerHTML = `
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        <span>사용자가 성공적으로 삭제되었습니다!</span>
      `;
      document.body.appendChild(toast);
      setTimeout(() => document.body.removeChild(toast), 3000);
    } catch (error) {
      console.error('사용자 삭제 실패:', error);
      
      // 오류 토스트 표시
      const toast = document.createElement('div');
      toast.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2';
      toast.innerHTML = `
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <span>사용자 삭제에 실패했습니다.</span>
      `;
      document.body.appendChild(toast);
      setTimeout(() => document.body.removeChild(toast), 5000);
    }
  };

  const handleStatusChange = async (userId: string, newStatus: 'active' | 'suspended' | 'banned') => {
    try {
      setUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, status: newStatus } : user
      ));
      
      // 성공 토스트 표시
      const statusText = newStatus === 'active' ? '활성' : newStatus === 'suspended' ? '정지' : '차단';
      const toast = document.createElement('div');
      toast.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2';
      toast.innerHTML = `
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        <span>사용자 상태가 '${statusText}'로 변경되었습니다!</span>
      `;
      document.body.appendChild(toast);
      setTimeout(() => document.body.removeChild(toast), 3000);
    } catch (error) {
      console.error('상태 변경 실패:', error);
      
      // 오류 토스트 표시
      const toast = document.createElement('div');
      toast.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2';
      toast.innerHTML = `
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <span>상태 변경에 실패했습니다.</span>
      `;
      document.body.appendChild(toast);
      setTimeout(() => document.body.removeChild(toast), 5000);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'suspended': return 'bg-yellow-100 text-yellow-800';
      case 'banned': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'bronze': return 'bg-orange-100 text-orange-800';
      case 'silver': return 'bg-gray-100 text-gray-800';
      case 'gold': return 'bg-yellow-100 text-yellow-800';
      case 'platinum': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'suspended': return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'banned': return <XCircle className="w-4 h-4 text-red-600" />;
      default: return <Clock className="w-4 h-4 text-gray-600" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTimeAgo = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return '방금 전';
    if (diffInHours < 24) return `${diffInHours}시간 전`;
    return `${Math.floor(diffInHours / 24)}일 전`;
  };

  const loadUserDetails = async (userId: string) => {
    try {
      const user = users.find(u => u.id === userId);
      if (!user) return;

      // 실제 API 호출로 자산 정보 가져오기
      try {
        const assetsRes: any = await voltronApiService.getUserAssets(userId);
        const balances = assetsRes.assets?.database || assetsRes.assets || {};
        
        const vttnBalance = parseFloat(balances.vttn?.toString() || balances.vttn_balance?.toString() || user.vttnBalance?.toString() || '0');
        const usdtBalance = parseFloat(balances.usdt?.toString() || balances.usdt_balance?.toString() || user.usdtBalance?.toString() || '0');
        
        const assets: UserAsset[] = [
          {
            token: 'VTTN',
            symbol: 'VTTN',
            balance: vttnBalance,
            value: vttnBalance,
            percentage: 0
          },
          {
            token: 'USDT',
            symbol: 'USDT',
            balance: usdtBalance,
            value: usdtBalance,
            percentage: 0
          }
        ];
        
        const totalValue = assets.reduce((sum, a) => sum + (a.value || 0), 0) || 1;
        const assetsWithPct = assets.map((a) => ({
          ...a,
          percentage: Math.round(((a.value || 0) / totalValue) * 100)
        }));
        
        setUserAssets(assetsWithPct);
      } catch (assetsError) {
        console.error('자산 정보 로드 실패:', assetsError);
        // 실패 시 사용자 기본 정보 사용
        const assets: UserAsset[] = [
          {
            token: 'VTTN',
            symbol: 'VTTN',
            balance: user.vttnBalance || 0,
            value: user.vttnBalance || 0,
            percentage: 0
          },
          {
            token: 'USDT',
            symbol: 'USDT',
            balance: user.usdtBalance || 0,
            value: user.usdtBalance || 0,
            percentage: 0
          }
        ];
        setUserAssets(assets);
      }

      // 실제 API 호출로 거래 내역 가져오기
      try {
        const txRes: any = await voltronApiService.getUserTransactions(userId, { page: 1, limit: 50 });
        const txs = (txRes.transactions || txRes.data?.transactions || []).map((t: any): UserTransaction => ({
          id: t.id?.toString() || t.tx_hash || `${t.created_at}`,
          type: t.transaction_type || t.type || 'transfer',
          amount: parseFloat(t.amount ?? 0),
          token: t.token_type || t.token || 'VTTN',
          status: t.status || 'completed',
          timestamp: t.created_at || t.timestamp,
          hash: t.tx_hash || t.hash
        }));
        setUserTransactions(txs);
      } catch (txError) {
        console.error('거래 내역 로드 실패:', txError);
        setUserTransactions([]);
      }

      // 실제 API 호출로 이벤트 내역 가져오기
      try {
        const eventsRes: any = await (voltronApiService as any).getUserEvents?.(userId);
        if (eventsRes && eventsRes.success && Array.isArray(eventsRes.events)) {
          const events: UserEvent[] = eventsRes.events.map((e: any) => ({
            id: e.id?.toString() || `${e.eventType}-${e.participatedAt}`,
            type: e.type || e.eventType || 'login',
            description: e.description || e.eventName || '이벤트',
            timestamp: e.timestamp || e.participatedAt || new Date().toISOString()
          }));
          setUserEvents(events);
        } else {
          setUserEvents([]);
        }
      } catch (eventsError) {
        console.warn('이벤트 내역 로드 실패:', eventsError);
        setUserEvents([]);
      }
    } catch (error) {
      console.error('Failed to load user details:', error);
    }
  };

  const loadReferralTree = async (userId: string) => {
    try {
      const user = users.find(u => u.id === userId);
      if (!user) return;

      // 실제 API 호출로 추천인 트리 가져오기
      const res: any = await voltronApiService.getUserReferralTree(userId);
      if (res && res.success && res.referralTree) {
        setReferralTree(res.referralTree as ReferralNode);
      } else if (res && res.referralTree) {
        // success 필드가 없어도 referralTree가 있으면 사용
        setReferralTree(res.referralTree as ReferralNode);
      } else {
        // 추천인이 없는 경우 빈 트리
        setReferralTree({
          user,
          level: 0,
          totalReferred: 0,
          totalVolume: 0,
          children: []
        });
      }
    } catch (error) {
      console.error('Failed to load referral tree:', error);
      // 에러 발생 시 빈 트리 설정
      const user = users.find(u => u.id === userId);
      if (user) {
        setReferralTree({
          user,
          level: 0,
          totalReferred: 0,
          totalVolume: 0,
          children: []
        });
      }
    }
  };

  const exportUsers = () => {
    const csvContent = [
      ['ID', 'Address', 'Status', 'Tier', 'Joined', 'Last Active', 'Transactions', 'Volume', 'Staking'],
      ...filteredUsers.map(user => [
        user.id,
        user.address,
        user.status,
        user.tier,
        new Date(user.joinedAt).toLocaleDateString(),
        user.lastActive ? new Date(user.lastActive).toLocaleDateString() : 'N/A',
        user.totalTransactions.toString(),
        user.totalVolume.toString(),
        user.stakingAmount.toString()
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `users-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // 표시할 사용자 목록 결정
  const usersToDisplay = showMissingUsersOnly ? missingUsers : allUsers;

  // 검색은 백엔드에서 처리하므로 프론트엔드 필터링은 상태/티어만 적용 (이미 백엔드에서 필터링됨)
  let filteredUsers = usersToDisplay.filter(user => {
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    const matchesTier = tierFilter === 'all' || user.tier === tierFilter;
    return matchesStatus && matchesTier;
  });

  // 정렬 적용 (전체 데이터에 대해)
  filteredUsers = [...filteredUsers].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'balance':
        comparison = (a.vttnBalance || 0) - (b.vttnBalance || 0);
        break;
      case 'referrals':
        comparison = (a.referralCount || 0) - (b.referralCount || 0);
        break;
      case 'volume':
        comparison = (a.totalVolume || 0) - (b.totalVolume || 0);
        break;
      case 'recent':
        const dateA = new Date(a.joinedAt || 0).getTime();
        const dateB = new Date(b.joinedAt || 0).getTime();
        comparison = dateA - dateB;
        break;
    }
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  // 페이징 적용
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * usersPerPage,
    currentPage * usersPerPage
  );

  // 현재 페이지의 유저 목록 설정 (테이블 표시용)
  useEffect(() => {
    setUsers(paginatedUsers);
  }, [JSON.stringify(paginatedUsers), currentPage, sortBy, sortOrder]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (showDetailModal && selectedUser) {
    return (
      <UserDetailPage
        user={selectedUser}
        onClose={() => setShowDetailModal(false)}
        onUserUpdate={(updatedUser) => {
          setUsers(prev => prev.map(u => u.id === updatedUser.id ? updatedUser : u));
          setSelectedUser(updatedUser);
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">사용자 관리</h1>
            <p className="text-gray-600 mt-1">Voltron 네트워크 사용자를 관리하고 모니터링하세요</p>
          </div>
          <div className="flex items-center space-x-3">
            <button 
              onClick={() => setShowAirdropModal(true)}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <Gift className="w-4 h-4" />
              <span>에어드롭</span>
            </button>
            <button 
              onClick={() => setShowReferralRewardModal(true)}
              className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <Users className="w-4 h-4" />
              <span>추천인 보상</span>
            </button>
            <button 
              onClick={() => setShowCreateModal(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>사용자 생성</span>
            </button>
            <button 
              onClick={exportUsers}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export Users</span>
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalUsers.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Users</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeUsers.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">New Today</p>
              <p className="text-2xl font-bold text-gray-900">{stats.newUsersToday}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Suspended</p>
              <p className="text-2xl font-bold text-gray-900">{stats.suspendedUsers}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <Shield className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
          <div className="relative md:col-span-2 flex items-center space-x-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="닉네임, 지갑주소, 추천코드로 검색..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    setDebouncedSearchTerm(searchTerm);
                    setCurrentPage(1);
                  }
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
            <button
              onClick={() => {
                setDebouncedSearchTerm(searchTerm);
                setCurrentPage(1);
              }}
              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors flex items-center space-x-2"
            >
              <Search className="w-4 h-4" />
              <span>검색</span>
            </button>
          </div>
          
          <select
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value as 'balance' | 'referrals' | 'recent' | 'volume');
              setCurrentPage(1);
            }}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          >
            <option value="recent">최근순</option>
            <option value="balance">자산보유 순</option>
            <option value="referrals">추천인 수 순</option>
            <option value="volume">거래량 순</option>
          </select>
          
          <select
            value={sortOrder}
            onChange={(e) => {
              setSortOrder(e.target.value as 'asc' | 'desc');
              setCurrentPage(1);
            }}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          >
            <option value="desc">내림차순</option>
            <option value="asc">오름차순</option>
          </select>
          
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as 'all' | 'active' | 'suspended');
              setCurrentPage(1);
            }}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="banned">Banned</option>
          </select>
          
          <select
            value={tierFilter}
            onChange={(e) => {
              setTierFilter(e.target.value as 'all' | 'bronze' | 'silver' | 'gold' | 'platinum');
              setCurrentPage(1);
            }}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          >
            <option value="all">All Tiers</option>
            <option value="bronze">Bronze</option>
            <option value="silver">Silver</option>
            <option value="gold">Gold</option>
            <option value="platinum">Platinum</option>
          </select>

          <label className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              checked={showMissingUsersOnly}
              onChange={(e) => setShowMissingUsersOnly(e.target.checked)}
              className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
            />
            <span className="text-sm font-medium text-gray-700">에어드롭 미지급 회원만</span>
          </label>

          <div className="text-sm text-gray-600 flex items-center">
            Showing {paginatedUsers.length} of {filteredUsers.length} users
            {showMissingUsersOnly && loadingMissingUsers && <span className="ml-2 text-orange-500">(로딩 중...)</span>}
          </div>
        </div>
        
        {/* 페이징 컨트롤 */}
        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              페이지 {currentPage} / {totalPages} (총 {filteredUsers.length}명)
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                이전
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1 border rounded-lg text-sm ${
                      currentPage === pageNum
                        ? 'bg-orange-500 text-white border-orange-500'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                다음
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tier</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">VTTN Balance</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">KYC</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activity</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Volume</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Referrals</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">레퍼럴 총 자산</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 whitespace-nowrap">
                    <div className="flex items-center space-x-3">
                      <div className="p-1.5 bg-blue-100 rounded-lg">
                        <Users className="w-3.5 h-3.5 text-blue-600" />
                      </div>
                      <div>
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            loadUserDetails(user.id);
                            setShowDetailModal(true);
                          }}
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
                        >
                          {user.nickname}
                        </button>
                        <p className="text-xs text-gray-500">{user.address.slice(0, 10)}...{user.address.slice(-8)}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTierColor(user.tier)}`}>
                      {user.tier.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <div>
                      <p className="text-sm font-medium text-blue-600">{(user.vttnBalance || 0).toLocaleString()} VTTN</p>
                      {user.bonusBalance > 0 && (
                        <p className="text-xs text-orange-500">+{user.bonusBalance.toLocaleString()} bonus</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      user.kycStatus === 'verified' ? 'bg-green-100 text-green-800' :
                      user.kycStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      user.kycStatus === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {user.kycStatus === 'verified' ? '인증' : 
                       user.kycStatus === 'pending' ? '대기' : 
                       user.kycStatus === 'rejected' ? '거부' : '미인증'}
                    </span>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <div>
                      <p className="text-sm text-gray-900">{formatTimeAgo(user.lastActive)}</p>
                      <p className="text-xs text-gray-500">{user.totalTransactions} transactions</p>
                    </div>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <div>
                      <p className="text-sm text-gray-900">{user.totalVolume.toLocaleString()} VTTN</p>
                      <p className="text-xs text-gray-500">{user.stakingAmount.toLocaleString()} staked</p>
                    </div>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{user.referralCount || 0}</p>
                      {user.referralCode && (
                        <p className="text-xs text-gray-500 font-mono">{user.referralCode}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {user.totalVolume > 0 
                          ? `${(user.totalVolume / 1000).toFixed(0)}K VTTN`
                          : '0K VTTN'}
                      </p>
                      {user.referralCount > 0 && (
                        <p className="text-xs text-gray-500">추천인 {user.referralCount}명</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(user.status)}
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(user.status)}`}>
                        {user.status}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          loadUserDetails(user.id);
                          setShowDetailModal(true);
                        }}
                        className="p-1 text-blue-600 hover:text-blue-800"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setEditingUser(user);
                          setShowEditModal(true);
                        }}
                        className="p-1 text-green-600 hover:text-green-800"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setShowDeleteModal(true);
                        }}
                        className="p-1 text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 에어드롭 모달 */}
      {showAirdropModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">에어드롭 실행</h3>
              <button
                onClick={() => setShowAirdropModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  대상 선택
                </label>
                <select
                  value={airdropData.recipients}
                  onChange={(e) => setAirdropData({ ...airdropData, recipients: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="all">모든 활성 사용자</option>
                  <option value="specific">KYC 완료 사용자</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  토큰 타입
                </label>
                <select
                  value={airdropData.tokenType}
                  onChange={(e) => setAirdropData({ ...airdropData, tokenType: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="VTTN">VTTN</option>
                  <option value="USDT">USDT</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  개별 지급 금액
                </label>
                <input
                  type="number"
                  min="0"
                  value={airdropData.amount}
                  onChange={(e) => setAirdropData({ ...airdropData, amount: parseFloat(e.target.value) || 0 })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="지급할 금액"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  설명
                </label>
                <input
                  type="text"
                  value={airdropData.description}
                  onChange={(e) => setAirdropData({ ...airdropData, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="에어드롭 설명"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowAirdropModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleAirdrop}
                className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
              >
                실행
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 추천인 보상 분배 모달 */}
      {showReferralRewardModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">추천인 보상 분배</h3>
              <button
                onClick={() => setShowReferralRewardModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  분배할 사용자
                </label>
                <select
                  value={referralRewardData.fromUser}
                  onChange={(e) => setReferralRewardData({ ...referralRewardData, fromUser: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">사용자 선택</option>
                  {users.filter(user => user.bonusBalance > 0).map(user => (
                    <option key={user.id} value={user.id}>
                      {user.nickname} (보너스: {user.bonusBalance.toLocaleString()} VTTN)
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  분배 금액
                </label>
                <input
                  type="number"
                  min="0"
                  value={referralRewardData.amount}
                  onChange={(e) => setReferralRewardData({ ...referralRewardData, amount: parseFloat(e.target.value) || 0 })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="분배할 금액"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  락업 기간 (개월)
                </label>
                <input
                  type="number"
                  min="1"
                  value={referralRewardData.lockupPeriod}
                  onChange={(e) => setReferralRewardData({ ...referralRewardData, lockupPeriod: parseInt(e.target.value) || 10 })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowReferralRewardModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleReferralRewardDistribution}
                className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors"
              >
                분배
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 사용자 생성 모달 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <UserPlus className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">새 사용자 생성</h3>
                  <p className="text-sm text-gray-500">Voltron 네트워크에 새 사용자를 추가합니다</p>
                </div>
              </div>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* 기본 정보 */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-4">기본 정보</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        지갑 주소 *
                      </label>
                      <input
                        type="text"
                        value={newUser.address || ''}
                        onChange={(e) => setNewUser({ ...newUser, address: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="0x..."
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        닉네임 *
                      </label>
                      <input
                        type="text"
                        value={newUser.nickname || ''}
                        onChange={(e) => setNewUser({ ...newUser, nickname: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="사용자 닉네임"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        이메일
                      </label>
                      <input
                        type="email"
                        value={newUser.email || ''}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="user@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        전화번호
                      </label>
                      <input
                        type="tel"
                        value={newUser.phone || ''}
                        onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="+82-10-1234-5678"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        국가
                      </label>
                      <select
                        value={newUser.country || 'KR'}
                        onChange={(e) => setNewUser({ ...newUser, country: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      >
                        <option value="KR">대한민국</option>
                        <option value="US">미국</option>
                        <option value="JP">일본</option>
                        <option value="CN">중국</option>
                        <option value="SG">싱가포르</option>
                        <option value="OTHER">기타</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* 계정 설정 */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-4">계정 설정</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        상태
                      </label>
                      <select
                        value={newUser.status || 'active'}
                        onChange={(e) => setNewUser({ ...newUser, status: e.target.value as any })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      >
                        <option value="active">활성</option>
                        <option value="suspended">정지</option>
                        <option value="banned">차단</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        티어
                      </label>
                      <select
                        value={newUser.tier || 'bronze'}
                        onChange={(e) => setNewUser({ ...newUser, tier: e.target.value as any })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      >
                        <option value="bronze">Bronze</option>
                        <option value="silver">Silver</option>
                        <option value="gold">Gold</option>
                        <option value="platinum">Platinum</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        KYC 상태
                      </label>
                      <select
                        value={newUser.kycStatus || 'pending'}
                        onChange={(e) => setNewUser({ ...newUser, kycStatus: e.target.value as any })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      >
                        <option value="pending">대기중</option>
                        <option value="verified">인증완료</option>
                        <option value="rejected">거부됨</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        추천인 코드
                      </label>
                      <input
                        type="text"
                        value={newUser.referralCode || ''}
                        onChange={(e) => setNewUser({ ...newUser, referralCode: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="REF123"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        추천받은 사람
                      </label>
                      <input
                        type="text"
                        value={newUser.referredBy || ''}
                        onChange={(e) => setNewUser({ ...newUser, referredBy: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="추천인 지갑 주소"
                      />
                    </div>
                  </div>
                </div>

                {/* 잔액 정보 */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-4">잔액 정보</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        VTTN 잔액
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.000001"
                        value={newUser.vttnBalance || 0}
                        onChange={(e) => setNewUser({ ...newUser, vttnBalance: parseFloat(e.target.value) || 0 })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="0.000000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        VTM 잔액
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.000001"
                        value={newUser.vtmBalance || 0}
                        onChange={(e) => setNewUser({ ...newUser, vtmBalance: parseFloat(e.target.value) || 0 })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="0.000000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        USDT 잔액
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={newUser.usdtBalance || 0}
                        onChange={(e) => setNewUser({ ...newUser, usdtBalance: parseFloat(e.target.value) || 0 })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        보너스 잔액
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.000001"
                        value={newUser.bonusBalance || 0}
                        onChange={(e) => setNewUser({ ...newUser, bonusBalance: parseFloat(e.target.value) || 0 })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="0.000000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        락업 잔액
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.000001"
                        value={newUser.lockedBalance || 0}
                        onChange={(e) => setNewUser({ ...newUser, lockedBalance: parseFloat(e.target.value) || 0 })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="0.000000"
                      />
                    </div>
                  </div>
                </div>

                {/* 활동 정보 */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-4">활동 정보</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        총 거래 수
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={newUser.totalTransactions || 0}
                        onChange={(e) => setNewUser({ ...newUser, totalTransactions: parseInt(e.target.value) || 0 })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        총 거래량 ($)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={newUser.totalVolume || 0}
                        onChange={(e) => setNewUser({ ...newUser, totalVolume: parseFloat(e.target.value) || 0 })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        스테이킹 금액
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.000001"
                        value={newUser.stakingAmount || 0}
                        onChange={(e) => setNewUser({ ...newUser, stakingAmount: parseFloat(e.target.value) || 0 })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="0.000000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        추천인 수
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={newUser.referralCount || 0}
                        onChange={(e) => setNewUser({ ...newUser, referralCount: parseInt(e.target.value) || 0 })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        BOOM 참여 횟수
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={newUser.boomParticipation || 0}
                        onChange={(e) => setNewUser({ ...newUser, boomParticipation: parseInt(e.target.value) || 0 })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        에너지 스테이션 수
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={newUser.energyStations || 0}
                        onChange={(e) => setNewUser({ ...newUser, energyStations: parseInt(e.target.value) || 0 })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>

                {/* 보상 및 수수료 */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-4">보상 및 수수료</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        획득한 보상
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.000001"
                        value={newUser.rewardsEarned || 0}
                        onChange={(e) => setNewUser({ ...newUser, rewardsEarned: parseFloat(e.target.value) || 0 })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="0.000000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        수수료율 (%)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={newUser.commissionRate || 0}
                        onChange={(e) => setNewUser({ ...newUser, commissionRate: parseFloat(e.target.value) || 0 })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        페널티 금액
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.000001"
                        value={newUser.penaltyAmount || 0}
                        onChange={(e) => setNewUser({ ...newUser, penaltyAmount: parseFloat(e.target.value) || 0 })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="0.000000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        IP 주소
                      </label>
                      <input
                        type="text"
                        value={newUser.ipAddress || ''}
                        onChange={(e) => setNewUser({ ...newUser, ipAddress: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="192.168.1.1"
                      />
                    </div>
                  </div>
                </div>

                {/* 추가 설정 */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-4">추가 설정</h4>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="welcomeAirdrop"
                        checked={newUser.welcomeAirdropReceived || false}
                        onChange={(e) => setNewUser({ ...newUser, welcomeAirdropReceived: e.target.checked })}
                        className="w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500"
                      />
                      <label htmlFor="welcomeAirdrop" className="text-sm font-medium text-gray-700">
                        웰컴 에어드롭 수령
                      </label>
                    </div>
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="welcomeEvent"
                        checked={newUser.welcomeEventCompleted || false}
                        onChange={(e) => setNewUser({ ...newUser, welcomeEventCompleted: e.target.checked })}
                        className="w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500"
                      />
                      <label htmlFor="welcomeEvent" className="text-sm font-medium text-gray-700">
                        웰컴 이벤트 완료
                      </label>
                    </div>
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="biometricEnabled"
                        checked={newUser.biometricEnabled || false}
                        onChange={(e) => setNewUser({ ...newUser, biometricEnabled: e.target.checked })}
                        className="w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500"
                      />
                      <label htmlFor="biometricEnabled" className="text-sm font-medium text-gray-700">
                        생체인증 활성화
                      </label>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        메모
                      </label>
                      <textarea
                        value={newUser.notes || ''}
                        onChange={(e) => setNewUser({ ...newUser, notes: e.target.value })}
                        rows={3}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="사용자에 대한 추가 메모..."
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                취소
              </button>
              <button
                onClick={async () => {
                  try {
                    // 필수 필드 검증
                    if (!newUser.address || !newUser.nickname) {
                      alert('지갑 주소와 닉네임은 필수 입력 항목입니다.');
                      return;
                    }

                    // 백엔드 API 호출 (Voltron API 서비스 사용)
                    const payload = {
                      userCode: newUser.userCode || `USER_${Date.now()}`,
                      nickname: newUser.nickname,
                      address: newUser.address, // 백엔드에서 address로 받음
                      email: newUser.email,
                      phone: newUser.phone, // 백엔드에서 phone으로 받음
                      country: newUser.country || 'KR',
                      kycStatus: newUser.kycStatus || 'pending',
                      tier: newUser.tier?.toUpperCase() || 'BRONZE',
                      vttnBalance: newUser.vttnBalance || 0,
                      vtmBalance: newUser.vtmBalance || 0,
                      usdtBalance: newUser.usdtBalance || 0,
                      lockedBalance: newUser.lockedBalance || 0,
                      bonusBalance: newUser.bonusBalance || 0,
                      totalBalance:
                        (newUser.vttnBalance || 0) +
                        (newUser.vtmBalance || 0) +
                        (newUser.usdtBalance || 0),
                      referralCode: newUser.referralCode,
                      referredBy: newUser.referredBy,
                      totalTransactions: newUser.totalTransactions || 0,
                      totalVolume: newUser.totalVolume || 0,
                      stakingAmount: newUser.stakingAmount || 0,
                      referralCount: newUser.referralCount || 0,
                      boomParticipation: newUser.boomParticipation || 0,
                      energyStations: newUser.energyStations || 0,
                      rewardsEarned: newUser.rewardsEarned || 0,
                      commissionRate: newUser.commissionRate || 0,
                      penaltyAmount: newUser.penaltyAmount || 0,
                      welcomeAirdropReceived: newUser.welcomeAirdropReceived || false,
                      welcomeEventCompleted: newUser.welcomeEventCompleted || false,
                      biometricEnabled: newUser.biometricEnabled || false,
                      isActive: newUser.status === 'active',
                      ipAddress: newUser.ipAddress,
                      notes: newUser.notes
                    };

                    const result: any = await voltronApiService.createUser(payload);

                    if (result && result.success) {
                      // 성공 토스트 표시
                      const toast = document.createElement('div');
                      toast.className =
                        'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2';
                      toast.innerHTML = `
                          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                          <span>사용자가 성공적으로 생성되었습니다!</span>
                        `;
                      document.body.appendChild(toast);
                      setTimeout(() => document.body.removeChild(toast), 3000);

                      setShowCreateModal(false);
                      // 새 사용자 객체 초기화
                      setNewUser({
                        userCode: '',
                        nickname: '',
                        address: '',
                        status: 'active',
                        tier: 'bronze',
                        commissionRate: 0,
                        bonusBalance: 0,
                        penaltyAmount: 0,
                        totalTransactions: 0,
                        totalVolume: 0,
                        stakingAmount: 0,
                        referralCount: 0,
                        boomParticipation: 0,
                        energyStations: 0,
                        rewardsEarned: 0,
                        email: '',
                        phone: '',
                        country: 'KR',
                        kycStatus: 'pending',
                        referralCode: '',
                        referredBy: '',
                        vttnBalance: 0,
                        vtmBalance: 0,
                        usdtBalance: 0,
                        lockedBalance: 0,
                        welcomeAirdropReceived: false,
                        welcomeEventCompleted: false,
                        biometricEnabled: false,
                        ipAddress: '',
                        notes: ''
                      });
                      // 사용자 목록 새로고침
                      await loadAllUsers();
                    } else {
                      // API 호출 실패 토스트 표시
                      const toast = document.createElement('div');
                      toast.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2';
                      toast.innerHTML = `
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                        </svg>
                        <span>사용자 생성 실패: ${(result && result.message) || '알 수 없는 오류'}</span>
                      `;
                      document.body.appendChild(toast);
                      setTimeout(() => document.body.removeChild(toast), 5000);
                    }
                  } catch (error) {
                    console.error('사용자 생성 오류:', error);
                    // 오류 토스트 표시
                    const toast = document.createElement('div');
                    toast.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2';
                    toast.innerHTML = `
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <span>사용자 생성 중 오류가 발생했습니다.</span>
                    `;
                    document.body.appendChild(toast);
                    setTimeout(() => document.body.removeChild(toast), 5000);
                  }
                }}
                className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors"
              >
                생성
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 사용자 상세 모달 */}
      {showDetailModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{selectedUser.nickname}</h3>
                  <p className="text-sm text-gray-500 font-mono">{selectedUser.address}</p>
                </div>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="grid grid-cols-1 gap-6">
                {/* 기본 정보 */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">기본 정보</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 block">사용자 코드:</span>
                      <span className="font-medium">{selectedUser.userCode}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 block">이메일:</span>
                      <span className="font-medium">{selectedUser.email || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 block">전화번호:</span>
                      <span className="font-medium">{selectedUser.phone || selectedUser.phoneNumber || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 block">국가:</span>
                      <span className="font-medium">{selectedUser.country || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 block">IP 주소:</span>
                      <span className="font-medium font-mono">{selectedUser.ipAddress || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 block">가입일:</span>
                      <span className="font-medium">{selectedUser.joinedAt || selectedUser.createdAt}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 block">최근 활동:</span>
                      <span className="font-medium">{selectedUser.lastActive || selectedUser.lastLogin || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 block">상태:</span>
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(selectedUser.status)}`}>
                        {selectedUser.status === 'active' ? '활성' : 
                         selectedUser.status === 'suspended' ? '정지' : '차단'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 block">티어:</span>
                      <span className={`px-2 py-1 rounded text-xs ${getTierColor(selectedUser.tier)}`}>
                        {selectedUser.tier.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 block">KYC 상태:</span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        selectedUser.kycStatus === 'verified' ? 'bg-green-100 text-green-800' :
                        selectedUser.kycStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {selectedUser.kycStatus === 'verified' ? '인증완료' : 
                         selectedUser.kycStatus === 'pending' ? '대기중' : '거부됨'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 block">생체인증:</span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        selectedUser.biometricEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {selectedUser.biometricEnabled ? '활성화' : '비활성화'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 block">활성 상태:</span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        selectedUser.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {selectedUser.isActive ? '활성' : '비활성'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* 잔액 정보 */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">잔액 정보</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 block">VTTN 잔액:</span>
                      <span className="font-medium text-blue-600">{(selectedUser.vttnBalance || 0).toLocaleString()} VTTN</span>
                    </div>
                    <div>
                      <span className="text-gray-600 block">VTM 잔액:</span>
                      <span className="font-medium text-purple-600">{(selectedUser.vtmBalance || 0).toLocaleString()} VTM</span>
                    </div>
                    <div>
                      <span className="text-gray-600 block">USDT 잔액:</span>
                      <span className="font-medium text-green-600">{(selectedUser.usdtBalance || 0).toLocaleString()} USDT</span>
                    </div>
                    <div>
                      <span className="text-gray-600 block">보너스 잔액:</span>
                      <span className="font-medium text-orange-600">{(selectedUser.bonusBalance || 0).toLocaleString()} VTTN</span>
                    </div>
                    <div>
                      <span className="text-gray-600 block">락업 잔액:</span>
                      <span className="font-medium text-red-600">{(selectedUser.lockedBalance || 0).toLocaleString()} VTTN</span>
                    </div>
                    <div>
                      <span className="text-gray-600 block">총 잔액:</span>
                      <span className="font-bold text-gray-900">{(selectedUser.totalBalance || 0).toLocaleString()} VTTN</span>
                    </div>
                  </div>
                </div>

                {/* 활동 정보 */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">활동 정보</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 block">총 거래 수:</span>
                      <span className="font-medium">{(selectedUser.totalTransactions || 0).toLocaleString()}회</span>
                    </div>
                    <div>
                      <span className="text-gray-600 block">총 거래량:</span>
                      <span className="font-medium">{(selectedUser.totalVolume || 0).toLocaleString()} VTTN</span>
                    </div>
                    <div>
                      <span className="text-gray-600 block">스테이킹 금액:</span>
                      <span className="font-medium">{(selectedUser.stakingAmount || 0).toLocaleString()} VTTN</span>
                    </div>
                    <div>
                      <span className="text-gray-600 block">추천인 수:</span>
                      <span className="font-medium">{(selectedUser.referralCount || 0).toLocaleString()}명</span>
                    </div>
                    <div>
                      <span className="text-gray-600 block">BOOM 참여:</span>
                      <span className="font-medium">{(selectedUser.boomParticipation || 0).toLocaleString()}회</span>
                    </div>
                    <div>
                      <span className="text-gray-600 block">에너지 스테이션:</span>
                      <span className="font-medium">{(selectedUser.energyStations || 0).toLocaleString()}개</span>
                    </div>
                  </div>
                </div>

                {/* 추천 정보 */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">추천 정보</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 block">추천인 코드:</span>
                      <span className="font-medium font-mono">{selectedUser.referralCode || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 block">추천받은 사람:</span>
                      <span className="font-medium">{selectedUser.referredBy || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                {/* 보상 및 수수료 */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">보상 및 수수료</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 block">획득한 보상:</span>
                      <span className="font-medium text-green-600">{(selectedUser.rewardsEarned || 0).toLocaleString()} VTTN</span>
                    </div>
                    <div>
                      <span className="text-gray-600 block">수수료율:</span>
                      <span className="font-medium">{(selectedUser.commissionRate || 0)}%</span>
                    </div>
                    <div>
                      <span className="text-gray-600 block">페널티 금액:</span>
                      <span className="font-medium text-red-600">{(selectedUser.penaltyAmount || 0).toLocaleString()} VTTN</span>
                    </div>
                  </div>
                </div>

                {/* 추가 설정 */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">추가 설정</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 block">웰컴 에어드롭 수령:</span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        selectedUser.welcomeAirdropReceived ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {selectedUser.welcomeAirdropReceived ? '수령완료' : '미수령'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 block">웰컴 이벤트 완료:</span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        selectedUser.welcomeEventCompleted ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {selectedUser.welcomeEventCompleted ? '완료' : '미완료'}
                      </span>
                    </div>
                  </div>
                  {selectedUser.notes && (
                    <div className="mt-4">
                      <span className="text-gray-600 block mb-1">메모:</span>
                      <div className="bg-white p-3 rounded border text-sm">{selectedUser.notes}</div>
                    </div>
                  )}
                </div>
                
                {/* 보상 관리 */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-4">보상 관리</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        보너스 지급
                      </label>
                      <div className="flex space-x-2">
                        <input
                          type="number"
                          min="0"
                          placeholder="지급할 금액"
                          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          id="bonusInput"
                        />
                        <button
                          onClick={async () => {
                            const input = document.getElementById('bonusInput') as HTMLInputElement;
                            const amount = parseFloat(input.value);
                            if (amount > 0) {
                              try {
                                // API 호출로 실제 트랜잭션 생성
                                const response = await fetch(buildApiUrl('/admin/users/bonus'), {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                  },
                                  body: JSON.stringify({
                                    user_address: selectedUser.address,
                                    amount: amount,
                                    token_type: 'VTTN',
                                    description: '관리자 보너스 지급'
                                  })
                                });
                                
                                if (response.ok) {
                                  const result = await response.json();
                                  if (result.success) {
                                    // 로컬 상태 업데이트
                                    const updatedUser = { ...selectedUser, bonusBalance: selectedUser.bonusBalance + amount };
                                    setSelectedUser(updatedUser);
                                    setUsers(prev => prev.map(user => 
                                      user.id === selectedUser.id ? updatedUser : user
                                    ));
                                    input.value = '';
                                    alert(`${amount.toLocaleString()} VTTN 보너스가 지급되었습니다.`);
                                  } else {
                                    alert('보너스 지급에 실패했습니다.');
                                  }
                                } else {
                                  alert('API 호출에 실패했습니다.');
                                }
                              } catch (error) {
                                console.error('보너스 지급 오류:', error);
                                alert('보너스 지급 중 오류가 발생했습니다.');
                              }
                            }
                          }}
                          className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                        >
                          지급
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        차감 관리
                      </label>
                      <div className="flex space-x-2">
                        <input
                          type="number"
                          min="0"
                          placeholder="차감할 금액"
                          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          id="penaltyInput"
                        />
                        <button
                          onClick={async () => {
                            const input = document.getElementById('penaltyInput') as HTMLInputElement;
                            const amount = parseFloat(input.value);
                            if (amount > 0) {
                              try {
                                // API 호출로 실제 트랜잭션 생성
                                const response = await fetch(buildApiUrl('/admin/users/penalty'), {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                  },
                                  body: JSON.stringify({
                                    user_address: selectedUser.address,
                                    amount: amount,
                                    token_type: 'VTTN',
                                    reason: '관리자 차감'
                                  })
                                });
                                
                                if (response.ok) {
                                  const result = await response.json();
                                  if (result.success) {
                                    // 로컬 상태 업데이트
                                    const updatedUser = { ...selectedUser, penaltyAmount: selectedUser.penaltyAmount + amount };
                                    setSelectedUser(updatedUser);
                                    setUsers(prev => prev.map(user => 
                                      user.id === selectedUser.id ? updatedUser : user
                                    ));
                                    input.value = '';
                                    alert(`${amount.toLocaleString()} VTTN이 차감되었습니다.`);
                                  } else {
                                    alert('차감에 실패했습니다.');
                                  }
                                } else {
                                  alert('API 호출에 실패했습니다.');
                                }
                              } catch (error) {
                                console.error('차감 오류:', error);
                                alert('차감 중 오류가 발생했습니다.');
                              }
                            }
                          }}
                          className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                        >
                          차감
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPage;